import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react';
import { useProjectStore } from '../../store/useProjectStore';
import { useAssignmentStore } from '../../store/useAssignmentStore';
import { useAuthStore } from '../../store/useAuthStore';
import * as api from '../../api/endpoints';
import type { PageAssignment } from '../../api/endpoints';
import type { ReviewBoqItem, LvMatch, BoqPosition, MatchSummary } from '../../types';
import { assigneeColor } from '../../utils/assigneeColor';
import { SyntaxCheckModal } from './SyntaxCheckModal';
import { CopyToOrganizationModal } from './CopyToOrganizationModal';
import type { CopyPhase } from './CopyToOrganizationModal';
import { SelectAllCompletedButton } from './SelectAllCompletedButton';
import { LayoutViewer } from './LayoutViewer';

/* ═══════════════════════════════════════════════════════════════
   Main AnnotationTab — card-based assignment view
   ═══════════════════════════════════════════════════════════════ */

// Normalize a filename for matching agent files against Monco upload
// file names: basename, lowercased, extension stripped. Monco may store
// the source as a different path/key, so we match on the stem only.
function normName(f: string): string {
  const base = (f.split('/').pop() || f).trim();
  const dot = base.lastIndexOf('.');
  return (dot > 0 ? base.slice(0, dot) : base).toLowerCase();
}

// Layout records are keyed by the file stem (basename minus extension, case
// kept), mirroring the backend's Path(file).stem.
function fileStem(f: string): string {
  const base = f.split('/').pop() || f;
  const dot = base.lastIndexOf('.');
  return dot > 0 ? base.slice(0, dot) : base;
}

interface LayoutBadge {
  state: 'ready' | 'unscaled' | 'none' | 'processing' | 'queued' | 'failed';
  label: string;
  tooltip: string;
}

// One badge per file from its per-page layout records: anything running wins,
// then queued, then failures; then done: ready when every page has a scaled
// layout, unscaled when some layout lacks a scale, none when no page has one.
function layoutBadge(st: api.LayoutStatusResponse | null, file: string): LayoutBadge | null {
  const pages = st?.files?.[fileStem(file)]?.pages;
  if (!pages) return null;
  const entries = Object.entries(pages).sort((a, b) => Number(a[0]) - Number(b[0]));
  if (entries.length === 0) return null;
  const total = entries.length;
  const ok = entries.filter(([, p]) => p.status === 'ok').length;
  const unscaled = entries.filter(([, p]) => p.status === 'ok' && p.warnings?.includes('no_scale')).length;
  const done = entries.filter(([, p]) => p.status === 'ok' || p.status === 'no_layout').length;
  const has = (...s: api.LayoutPageStatus[]) => entries.some(([, p]) => s.includes(p.status));
  const describe: Record<api.LayoutPageStatus, string> = {
    ok: 'layout ready', no_layout: 'no layout found on this page', queued: 'queued',
    processing: 'processing', failed: 'failed', stale: 'stalled', unknown: 'unreadable',
  };
  const lines = entries.map(([n, p]) => {
    const what = p.status === 'ok' && p.warnings?.includes('no_scale') ? 'layout without scale' : describe[p.status] ?? p.status;
    const why = p.error || (p.status !== 'ok' || p.warnings?.length ? p.reason : '');
    return `p.${n}: ${what}${why ? ` — ${why}` : ''}`;
  });
  const tooltip = `Floor-plan layout detection\n${lines.join('\n')}`;
  const frac = total > 1 ? ` ${done}/${total}` : '';
  if (has('processing')) return { state: 'processing', label: `layout${frac}…`, tooltip };
  if (has('queued')) return { state: 'queued', label: 'layout queued', tooltip };
  if (has('failed', 'stale', 'unknown')) return { state: 'failed', label: `layout failed${frac}`, tooltip };
  if (ok === 0) return { state: 'none', label: `no layout${frac}`, tooltip };
  if (unscaled > 0) return { state: 'unscaled', label: total > 1 ? `layout ${ok}/${total}` : 'layout', tooltip };
  return { state: 'ready', label: total > 1 ? `layout ${ok}/${total}` : 'layout', tooltip };
}

// What the card's layout button offers for a file, from its triage floor-plan
// pages and their records. Running wins (disabled); then failed/stale pages
// (retry); then pages with no record (extract). null = nothing to offer: no
// floor-plan pages, or every one is done.
interface LayoutAction {
  kind: 'extract' | 'retry' | 'running';
  pages: number;
}
const LAYOUT_RETRY: api.LayoutPageStatus[] = ['failed', 'stale', 'unknown', 'no_layout'];
function layoutAction(st: api.LayoutStatusResponse | null, file: string): LayoutAction | null {
  const stem = fileStem(file);
  const qual = st?.floor_plans?.[stem] ?? [];
  if (qual.length === 0) return null;
  const recs = st?.files?.[stem]?.pages ?? {};
  const statuses = qual.map((n) => recs[String(n)]?.status);
  const running = statuses.filter((s) => s === 'queued' || s === 'processing').length;
  if (running) return { kind: 'running', pages: running };
  const retry = statuses.filter((s) => s && LAYOUT_RETRY.includes(s)).length;
  const missing = statuses.filter((s) => !s).length;
  if (retry) return { kind: 'retry', pages: retry + missing };
  if (missing) return { kind: 'extract', pages: missing };
  return null;
}

// Friendlier upload-group title: a Monco upload name is often an S3 prefix
// ("smart-surveyor/projects/196/files"); show its meaningful tail instead.
const GENERIC_SEGMENTS = new Set(['files', 'uploads', 'upload', 'input', 'inputs', 'pdfs']);
function friendlyUploadName(u: api.MoncoUpload): string {
  const full = (u.name || '').trim();
  if (!full) return `Upload ${u.id}`;
  const segs = full.split('/').filter(Boolean);
  while (segs.length > 1 && GENERIC_SEGMENTS.has(segs[segs.length - 1].toLowerCase())) segs.pop();
  const last = segs[segs.length - 1] ?? full;
  const prev = segs[segs.length - 2]?.toLowerCase();
  if (/^\d+$/.test(last) && (prev === 'projects' || prev === 'project')) return `Project ${last}`;
  return last;
}

export function AnnotationTab() {
  const isOwner = useAuthStore((s) => s.user?.role) === 'owner';
  const assignments = useAssignmentStore((s) => s.assignments);
  const assignmentsLoaded = useAssignmentStore((s) => s.loaded);
  const projectId = useProjectStore((s) => s.project?.project_id);
  const projectName = useProjectStore((s) => s.project?.name);

  const [detailAssignment, setDetailAssignment] = useState<PageAssignment | null>(null);
  const [syntaxOpen, setSyntaxOpen] = useState(false);
  const [publishStatus, setPublishStatus] = useState<Record<string, { published_at?: string; floor_plan_id?: number; publish_status?: api.PlanPublishStatus }>>({});
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [batchMatching, setBatchMatching] = useState(false);
  // Live match-all progress (per-plan pending/running/done/failed), polled
  // while a background run is active so cards show what's happening.
  const [matchAllSt, setMatchAllSt] = useState<api.MatchAllStatus | null>(null);
  const matchPollRef = useRef<number | null>(null);
  // Plans the agent is processing by itself after an annotator completed them
  // (#166). Polled continuously rather than only while we started something:
  // this work is triggered from the annotator's app, so nothing on this side
  // would otherwise know it had begun.
  const [autoProc, setAutoProc] = useState<api.AutoProcessStatus | null>(null);
  // Per-file S3 added date (basename → ISO), so cards can show how new a
  // file is. Empty for local projects; best-effort, never blocks the tab.
  const [fileAddedDates, setFileAddedDates] = useState<Record<string, string>>({});
  // Layout-detection status per file (owner only). Polled: Batch jobs
  // finish on their own; the server caches the S3 reads briefly.
  const [layoutSt, setLayoutSt] = useState<api.LayoutStatusResponse | null>(null);
  // File stems whose "Extract layout" preview/confirm/submit is in flight.
  const [layoutBusy, setLayoutBusy] = useState<Set<string>>(new Set());

  // Monco upload grouping + project/upload status (owner only).
  const [moncoUploads, setMoncoUploads] = useState<api.MoncoUpload[]>([]);
  const [moncoProjectStatus, setMoncoProjectStatus] = useState<api.MoncoStatus | null>(null);
  const [moncoProjectId, setMoncoProjectId] = useState<number | null>(null);
  const [moncoWebBase, setMoncoWebBase] = useState<string>('');
  const [moncoLoadError, setMoncoLoadError] = useState<string | null>(null);
  const [statusBusy, setStatusBusy] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  // Per-group "Upload all to DB" progress, keyed by section key.
  const [uploadAll, setUploadAll] = useState<Record<string, { done: number; total: number }>>({});
  // "Copy to another organization" (BETA) — modal + in-flight progress.
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [orgCopy, setOrgCopy] = useState<{ phase: CopyPhase; done: number; total: number } | null>(null);
  // Owners wait on the Monco fetch (project status + upload grouping)
  // before the tab renders, so cards don't flash ungrouped first.
  const [moncoLoading, setMoncoLoading] = useState(true);

  // Load publish status
  useEffect(() => {
    if (!isOwner) return;
    api.getPlanPublishStatus().then((s) => s && setPublishStatus(s));
  }, [isOwner]);

  // Poll match-all status while a background run is active. Also checked
  // once on mount so navigating back to the tab picks up a run already
  // in flight.
  const stopMatchPoll = useCallback(() => {
    if (matchPollRef.current != null) {
      window.clearInterval(matchPollRef.current);
      matchPollRef.current = null;
    }
  }, []);
  const startMatchPoll = useCallback(() => {
    if (matchPollRef.current != null) return;
    matchPollRef.current = window.setInterval(async () => {
      const st = await api.matchAllPlansStatus().catch(() => null);
      if (st) setMatchAllSt(st);
      if (!st || st.status !== 'running') {
        stopMatchPoll();
        if (st?.status === 'completed') {
          const fails = st.errors?.length || 0;
          useProjectStore.getState().setToast(
            fails
              ? `Plan matching finished — ${fails} plan${fails !== 1 ? 's' : ''} failed`
              : 'Plan matching finished',
            fails ? 'error' : 'success',
          );
        }
      }
    }, 2000);
  }, [stopMatchPoll]);
  useEffect(() => {
    if (!isOwner) return;
    api.matchAllPlansStatus().then((st) => {
      if (st && st.status !== 'idle') {
        setMatchAllSt(st);
        if (st.status === 'running') startMatchPoll();
      }
    }).catch(() => undefined);
    return stopMatchPoll;
  }, [isOwner, startMatchPoll, stopMatchPoll]);

  // Poll auto-processing state. Cheap (in-memory on the server) and always
  // on, because a plan can start processing at any moment without this tab
  // having done anything.
  useEffect(() => {
    if (!isOwner) return;
    let cancelled = false;
    const tick = () => {
      api.annotationProcessingStatus()
        .then((st) => { if (!cancelled) setAutoProc(st); })
        .catch(() => undefined);
    };
    tick();
    const id = window.setInterval(tick, 4000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [isOwner]);

  // A plan being processed is off-limits for manual match / syntax check, and
  // while ANY plan is processing the project-wide actions are held back —
  // both write the same published_plans.json and annotation state.
  const autoByPlan = new Map<string, api.AutoProcessPlan>(
    (autoProc?.plans ?? []).map((p) => [`${p.file}:${p.page}`, p]),
  );
  const autoBusy = !!autoProc?.busy;

  // Poll layout-detection status. Layout only runs when the owner clicks
  // "Extract layout"; that click refreshes at once so 'queued' shows up
  // without waiting for the next tick.
  const layoutProjectRef = useRef(projectId);
  layoutProjectRef.current = projectId;
  const refreshLayout = useCallback(() => {
    if (!isOwner || !projectId) return;
    api.pmGetLayoutStatus(projectId)
      .then((st) => { if (st && layoutProjectRef.current === projectId) setLayoutSt(st); })
      .catch(() => undefined);
  }, [isOwner, projectId]);
  useEffect(() => {
    if (!isOwner || !projectId) return;
    refreshLayout();
    const id = window.setInterval(refreshLayout, 20000);
    return () => window.clearInterval(id);
  }, [isOwner, projectId, refreshLayout]);

  // Owner click: preview what would be submitted (the confirm shows the
  // count), then submit. `pages` narrows to those pages (the page modal).
  const handleExtractLayout = async (file: string, pages?: number[]) => {
    if (!projectId) return;
    const stem = fileStem(file);
    const toast = useProjectStore.getState().setToast;
    setLayoutBusy((b) => new Set(b).add(stem));
    try {
      const pre = await api.pmLayoutExtractPreview(projectId, file, pages);
      if (!pre.ok || !pre.data) {
        toast(`Layout preview failed: ${pre.error}`, 'error');
        return;
      }
      const n = pre.data.submitted.length;
      if (n === 0) {
        toast('Nothing to extract: every floor-plan page already has a layout or one is running', 'success');
        refreshLayout();
        return;
      }
      const name = file.split('/').pop() || file;
      if (!window.confirm(
        `Extract layout for ${n} floor-plan page${n !== 1 ? 's' : ''} of ${name}?\n\n`
        + 'About $0.30–0.80 Gemini per page, 2–6 min each.',
      )) return;
      const r = await api.pmLayoutExtract(projectId, file, pages);
      if (!r.ok || !r.data) {
        toast(`Layout extraction failed: ${r.error}`, 'error');
      } else {
        const sent = r.data.submitted.length;
        const failed = r.data.failed.length;
        toast(
          failed
            ? `Layout: ${sent} page${sent !== 1 ? 's' : ''} submitted, ${failed} failed to submit`
            : `Layout extraction started for ${sent} page${sent !== 1 ? 's' : ''}`,
          failed ? 'error' : 'success',
        );
      }
      refreshLayout();
    } finally {
      setLayoutBusy((b) => { const next = new Set(b); next.delete(stem); return next; });
    }
  };

  // Load per-file S3 added dates for the active project. Best-effort.
  useEffect(() => {
    if (!projectId) return;
    api.getFileAddedDates().then((d) => setFileAddedDates(d || {}));
  }, [projectId]);

  // Load Monco uploads (for grouping) + project status. Best-effort:
  // if it fails (env unset, 403, no monco id) we fall back to a flat
  // list and surface a non-blocking notice.
  useEffect(() => {
    if (!isOwner) { setMoncoLoading(false); return; }
    setMoncoLoading(true);
    api.getMoncoUploads().then((r) => {
      if (r.ok && r.data) {
        setMoncoUploads(r.data.uploads);
        setMoncoProjectStatus(r.data.project_status);
        setMoncoProjectId(r.data.monco_project_id);
        setMoncoWebBase(r.data.web_base_url || '');
        setMoncoLoadError(null);
      } else {
        setMoncoLoadError(r.error || 'Could not load Monco uploads');
      }
    }).finally(() => setMoncoLoading(false));
  }, [isOwner]);

  const handleSetProjectStatus = async (next: api.MoncoStatus) => {
    if (next === moncoProjectStatus) return;
    const prev = moncoProjectStatus;
    setMoncoProjectStatus(next);
    setStatusBusy(true);
    const r = await api.setProjectStatus(next);
    setStatusBusy(false);
    if (r.ok && r.data) {
      setMoncoProjectStatus(r.data.status);
      useProjectStore.getState().setToast(`Project status → ${api.MONCO_STATUS_LABELS[r.data.status]}`, 'success');
    } else {
      setMoncoProjectStatus(prev);
      useProjectStore.getState().setToast(`Failed to set project status: ${r.error}`, 'error');
    }
  };

  const handleSetUploadStatus = async (uploadId: number, next: api.MoncoStatus) => {
    const upload = moncoUploads.find((u) => u.id === uploadId);
    if (!upload || next === upload.status) return;
    const prev = upload.status;
    setMoncoUploads((us) => us.map((u) => (u.id === uploadId ? { ...u, status: next } : u)));
    setStatusBusy(true);
    const r = await api.setUploadStatus(uploadId, next);
    setStatusBusy(false);
    if (r.ok && r.data) {
      const applied = r.data.status;
      setMoncoUploads((us) => us.map((u) => (u.id === uploadId ? { ...u, status: applied } : u)));
      useProjectStore.getState().setToast(`Upload ${uploadId} → ${api.MONCO_STATUS_LABELS[applied]}`, 'success');
    } else {
      setMoncoUploads((us) => us.map((u) => (u.id === uploadId ? { ...u, status: prev } : u)));
      useProjectStore.getState().setToast(`Failed to set upload status: ${r.error}`, 'error');
    }
  };

  const toggleCollapsed = (key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Project annotators — for reassigning a page to a different user from
  // any card (regardless of status). Reassign reuses the assign endpoint,
  // so it upserts the assignee AND re-sends the notification email.
  const [projectUsers, setProjectUsers] = useState<api.ProjectUser[]>([]);
  useEffect(() => {
    if (!isOwner || !projectId) return;
    api.pmGetProjectUsers(projectId).then((u) => u && setProjectUsers(u));
  }, [isOwner, projectId]);
  const annotators = useMemo(
    () => projectUsers.filter((u) => u.project_role === 'annotator'),
    [projectUsers],
  );

  const handleReassign = async (a: PageAssignment, newUserId: number) => {
    if (!projectId || newUserId === a.user_id) return;
    // Reassign endpoint changes the assignee only — status/work preserved
    // (a completed page stays completed) — and re-sends the email.
    const res = await api.pmReassignAssignment(a.id, newUserId);
    if (!res) {
      useProjectStore.getState().setToast('Reassign failed', 'error');
      return;
    }
    const name = annotators.find((u) => u.id === newUserId)?.username || 'annotator';
    useProjectStore.getState().setToast(`Reassigned to ${name} — notification sent`, 'success');
    await useAssignmentStore.getState().load(projectId);
  };

  // Copy this project into another organization: create a fresh Monco project
  // there, replay the per-plan upload into it, then import diagrams and publish.
  // The per-plan loop stays here (not one long request) for the same reason
  // "Upload all to DB" does — a single multi-minute POST hits the proxy timeout.
  const handleCopyToOrganization = async (organizationId: number, targetName: string) => {
    if (orgCopy) return; // already running
    setCopyModalOpen(false);
    setOrgCopy({ phase: 'creating', done: 0, total: 0 });

    const started = await api.orgCopyStart(organizationId, targetName);
    if (!started.ok || !started.data) {
      setOrgCopy(null);
      useProjectStore.getState().setToast(started.error || 'Copy failed to start', 'error');
      return;
    }
    const { project_id: targetProjectId, plans, objects_copied: objectsCopied } = started.data;

    setOrgCopy({ phase: 'uploading', done: 0, total: plans.length });
    let uploaded = 0;
    let failed = 0;
    for (let index = 0; index < plans.length; index++) {
      try {
        const res = await api.uploadPlanToDb(plans[index].file, plans[index].page, targetProjectId);
        if (res) uploaded++; else failed++;
      } catch {
        failed++;
      }
      setOrgCopy({ phase: 'uploading', done: index + 1, total: plans.length });
    }

    setOrgCopy({ phase: 'finishing', done: plans.length, total: plans.length });
    const finished = await api.orgCopyFinish(targetProjectId);
    setOrgCopy(null);

    if (!finished.ok) {
      useProjectStore.getState().setToast(
        `Copied ${uploaded}/${plans.length} plans to project #${targetProjectId}, but publishing failed: ${finished.error}`,
        'error',
      );
      return;
    }
    const diagramsNote = finished.data?.diagrams_error
      ? ` — diagrams skipped (${finished.data.diagrams_error})`
      : '';
    const statusNote = finished.data?.project_status_error
      ? ` — status not set to done (${finished.data.project_status_error})`
      : '';
    useProjectStore.getState().setToast(
      `Copied to organization ${organizationId} as project #${targetProjectId} — `
      + `${uploaded}/${plans.length} plans, ${objectsCopied} files`
      + `${failed ? `, ${failed} failed` : ''}${diagramsNote}${statusNote}`,
      failed ? 'error' : 'success',
    );
  };

  // Upload every COMPLETED assignment in a group to the Monco DB, one at
  // a time. Each call hits the same single-plan upload-to-db endpoint,
  // which re-uploads (replaces) plans that were already uploaded. Live
  // progress (done/total) is shown on the group's button.
  const handleUploadAllToDb = async (sectionKey: string, sectionAssignments: PageAssignment[]) => {
    if (uploadAll[sectionKey]) return; // already running
    const completed = sectionAssignments.filter((a) => a.status === 'completed');
    if (completed.length === 0) {
      useProjectStore.getState().setToast('No completed assignments to upload', 'error');
      return;
    }
    setUploadAll((p) => ({ ...p, [sectionKey]: { done: 0, total: completed.length } }));
    let ok = 0;
    let skipped = 0;
    let fail = 0;
    for (let i = 0; i < completed.length; i++) {
      const a = completed[i];
      const res = await api.uploadPlanToDbResult(a.file, a.page);
      if (res.ok) {
        ok++;
      } else if (res.status === 409) {
        // The plan is held right now (auto-processing or another user) —
        // it is being handled, not broken. Report it as skipped.
        skipped++;
      } else {
        fail++;
      }
      setUploadAll((p) => ({ ...p, [sectionKey]: { done: i + 1, total: completed.length } }));
    }
    // Refresh publish status so cards reflect their uploaded state.
    const s = await api.getPlanPublishStatus();
    if (s) setPublishStatus(s);
    setUploadAll((p) => {
      const next = { ...p };
      delete next[sectionKey];
      return next;
    });
    const parts = [
      skipped ? `${skipped} skipped (being auto-processed)` : '',
      fail ? `${fail} failed` : '',
    ].filter(Boolean).join(', ');
    useProjectStore.getState().setToast(
      `Uploaded ${ok}/${completed.length} to DB${parts ? ` — ${parts}` : ''}`,
      fail ? 'error' : 'success',
    );
  };

  // Publish every UPLOADED plan of a group in Monco (one PATCH per plan).
  // Plans without a floor_plan_id are skipped — they need "Upload to DB"
  // first. A 409 means the stored id went stale (record deleted in Monco);
  // those count as failed with the re-upload hint.
  const handlePublishAll = async (sectionKey: string, sectionAssignments: PageAssignment[]) => {
    const key = `pub:${sectionKey}`;
    if (uploadAll[key]) return; // already running
    const uploaded = sectionAssignments.filter(
      (a) => a.status === 'completed' && publishStatus[`${a.file}:${a.page}`]?.floor_plan_id,
    );
    if (uploaded.length === 0) {
      useProjectStore.getState().setToast('Nothing to publish — upload plans to the DB first', 'error');
      return;
    }
    setUploadAll((p) => ({ ...p, [key]: { done: 0, total: uploaded.length } }));
    let ok = 0;
    let stale = 0;
    let fail = 0;
    for (let i = 0; i < uploaded.length; i++) {
      const a = uploaded[i];
      const res = await api.setPlanPublishStatusResult(a.file, a.page, 'published');
      if (res.ok) ok++;
      else if ((res.error || '').includes('stale') || (res.error || '').includes('no longer exists')) stale++;
      else fail++;
      setUploadAll((p) => ({ ...p, [key]: { done: i + 1, total: uploaded.length } }));
    }
    const s = await api.getPlanPublishStatus();
    if (s) setPublishStatus(s);
    setUploadAll((p) => {
      const next = { ...p };
      delete next[key];
      return next;
    });
    const parts = [`Published ${ok}/${uploaded.length}`];
    if (stale) parts.push(`${stale} stale — re-upload to DB first`);
    if (fail) parts.push(`${fail} failed`);
    useProjectStore.getState().setToast(parts.join(' · '), (stale || fail) ? 'error' : 'success');
  };

  // Push every uploaded plan's 'ok' layout into its Monco floor plan, one
  // POST per plan (a single long request would hit the proxy timeout).
  // Plans without a floor_plan_id or without an 'ok' layout are left out.
  const layoutUploadCandidates = () => assignments.filter((a) =>
    a.status === 'completed'
    && publishStatus[`${a.file}:${a.page}`]?.floor_plan_id
    && layoutSt?.files?.[fileStem(a.file)]?.pages?.[String(a.page)]?.status === 'ok');
  const handleUploadAllLayouts = async () => {
    const key = 'layouts:all';
    if (uploadAll[key]) return; // already running
    const candidates = layoutUploadCandidates();
    if (candidates.length === 0) {
      useProjectStore.getState().setToast('No uploaded plan has a detected layout yet', 'error');
      return;
    }
    setUploadAll((p) => ({ ...p, [key]: { done: 0, total: candidates.length } }));
    let ok = 0;
    let stale = 0;
    let fail = 0;
    for (let i = 0; i < candidates.length; i++) {
      const a = candidates[i];
      const res = await api.uploadPlanLayoutToDbResult(a.file, a.page);
      if (res.ok) ok++;
      else if (res.status === 409) stale++;
      else fail++;
      setUploadAll((p) => ({ ...p, [key]: { done: i + 1, total: candidates.length } }));
    }
    setUploadAll((p) => {
      const next = { ...p };
      delete next[key];
      return next;
    });
    const parts = [`Uploaded ${ok}/${candidates.length} layouts`];
    if (stale) parts.push(`${stale} stale — re-upload to DB first`);
    if (fail) parts.push(`${fail} failed`);
    useProjectStore.getState().setToast(parts.join(' · '), (stale || fail) ? 'error' : 'success');
  };

  const toggleSelect = (id: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllCompleted = () => {
    const completedIds = assignments.filter(a => a.status === 'completed').map(a => a.id);
    const allSelected = completedIds.every(id => selected.has(id));
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(completedIds));
    }
  };

  // Add or remove one upload group's completed assignments, merging into the
  // existing selection rather than replacing it. The button decides the
  // direction (shouldSelect); here we just apply it.
  const setSectionSelection = (completedIds: number[], shouldSelect: boolean) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (shouldSelect) completedIds.forEach(id => next.add(id));
      else completedIds.forEach(id => next.delete(id));
      return next;
    });
  };

  // Fire one background job that matches ALL completed plans in parallel
  // server-side, then return immediately so the user can go work in Extraction.
  const handleBatchMatch = async () => {
    const completed = assignments.filter(a => a.status === 'completed');
    if (completed.length === 0) return;
    setBatchMatching(true);
    try {
      const res = await api.matchAllPlans();
      const n = (res && res.total) || completed.length;
      useProjectStore.getState().setToast(
        `Matching ${n} plan${n !== 1 ? 's' : ''} in the background — you can keep working.`, 'success');
      // Seed progress immediately so the button/cards react before the
      // first poll returns, then start polling.
      setMatchAllSt({ status: 'running', total: n, done: 0, plans: {} });
      startMatchPoll();
    } catch {
      useProjectStore.getState().setToast('Failed to start matching', 'error');
    } finally {
      setBatchMatching(false);
    }
  };

  // After the syntax-check modal applies renames. No toast: the modal stays open
  // and reports the outcome in its own banner.
  const handleSyntaxApplied = async () => {
    if (projectId) await useAssignmentStore.getState().load(projectId);
  };

  // Group by status
  const grouped = useMemo(() => {
    const groups: Record<string, PageAssignment[]> = {
      completed: [], in_progress: [], assigned: [], skipped: [],
    };
    for (const a of assignments) {
      (groups[a.status] ??= []).push(a);
    }
    return groups;
  }, [assignments]);

  const totalCompleted = grouped.completed.length;
  const layoutUploadProgress = uploadAll['layouts:all'];
  const layoutUploadCount = layoutUploadCandidates().length;
  const totalInProgress = grouped.in_progress.length;
  const total = assignments.length;

  // The selected plans as {file, page}, for scoping the Syntax Check modal.
  const selectedPlans = useMemo(
    () => assignments
      .filter(assignment => selected.has(assignment.id))
      .map(assignment => ({ file: assignment.file, page: assignment.page })),
    [assignments, selected],
  );

  // Bucket assignments into their Monco upload (by filename). Uploads
  // keep their API order; unmatched files land in a trailing "Ungrouped"
  // section.
  const uploadSections = useMemo(() => {
    const fileToUpload = new Map<string, number>();
    for (const u of moncoUploads) {
      for (const fn of u.file_names) fileToUpload.set(normName(fn), u.id);
    }
    const byUpload = new Map<number, PageAssignment[]>();
    const ungrouped: PageAssignment[] = [];
    for (const a of assignments) {
      const uid = fileToUpload.get(normName(a.file));
      if (uid != null) {
        const list = byUpload.get(uid);
        if (list) list.push(a);
        else byUpload.set(uid, [a]);
      } else {
        ungrouped.push(a);
      }
    }
    const sections: { key: string; upload: api.MoncoUpload | null; assignments: PageAssignment[] }[] = [];
    for (const u of moncoUploads) {
      const list = byUpload.get(u.id);
      if (list && list.length) sections.push({ key: `u${u.id}`, upload: u, assignments: list });
    }
    if (ungrouped.length) sections.push({ key: 'ungrouped', upload: null, assignments: ungrouped });
    return sections;
  }, [assignments, moncoUploads]);

  // Only switch to the grouped layout when at least one upload actually
  // matched assignments — otherwise (no monco data, or no filename
  // matches) keep the original flat grid.
  const useGrouped = isOwner && uploadSections.some((s) => s.upload);

  const emptyState = (
    <div className="annotation-empty">
      <div className="empty-icon">&#9633;</div>
      <div>No assignments yet</div>
      <div className="text-dim">Use the Team panel to assign floor plan pages to annotators</div>
    </div>
  );

  if (!assignmentsLoaded || (isOwner && moncoLoading)) {
    const msg = !assignmentsLoaded ? 'Loading assignments…' : 'Loading project status & uploads…';
    return (
      <div className="annotation-container">
        <div className="annotation-empty">
          <span className="ann-btn-spinner" />
          <div className="text-dim">{msg}</div>
        </div>
      </div>
    );
  }

  // Annotators with nothing assigned get the bare empty screen. Owners fall
  // through to the main render so the project-status control stays reachable.
  if (assignments.length === 0 && !isOwner) {
    return <div className="annotation-container">{emptyState}</div>;
  }

  const renderCard = (a: PageAssignment) => {
    const planKey = `${a.file}:${a.page}`;
    const base = a.file.split('/').pop() || a.file;
    const addedAt = fileAddedDates[a.file] || fileAddedDates[base];
    return (
      <AssignmentCard
        key={a.id}
        assignment={a}
        isOwner={isOwner}
        published={!!publishStatus[planKey]}
        selected={selected.has(a.id)}
        annotators={annotators}
        addedAt={addedAt}
        matchState={matchAllSt?.plans?.[planKey]}
        autoState={autoByPlan.get(planKey)}
        layout={isOwner ? layoutBadge(layoutSt, a.file) : null}
        layoutStatusLoaded={!!layoutSt}
        layoutPage={layoutSt?.files?.[fileStem(a.file)]?.pages?.[String(a.page)] ?? null}
        layoutQualifies={(layoutSt?.floor_plans?.[fileStem(a.file)]?.length ?? 0) > 0}
        layoutAction={isOwner ? layoutAction(layoutSt, a.file) : null}
        layoutBusy={layoutBusy.has(fileStem(a.file))}
        onExtractLayout={() => handleExtractLayout(a.file)}
        onReassign={(uid) => handleReassign(a, uid)}
        onToggleSelect={() => toggleSelect(a.id)}
        onOpenDetail={() => setDetailAssignment(a)}
      />
    );
  };

  return (
    <div className="annotation-container">
      {/* Stats bar */}
      <div className="ann-stats-bar">
        <div className="ann-stats-left">
          <span className="dim">{total} assignment{total !== 1 ? 's' : ''}</span>
          {totalCompleted > 0 && <span className="ann-stat-badge ann-stat-completed">{totalCompleted} completed</span>}
          {totalInProgress > 0 && <span className="ann-stat-badge ann-stat-inprogress">{totalInProgress} in progress</span>}
          {isOwner && moncoProjectStatus != null && (
            <span className="ann-project-status">
              <span className="dim">Project:</span>
              <select
                className="btn-review-action"
                value={moncoProjectStatus}
                disabled={statusBusy}
                onChange={(e) => handleSetProjectStatus(e.target.value as api.MoncoStatus)}
                title="Change the Monco project status"
              >
                {api.MONCO_STATUS_VALUES.map((s) => (
                  <option key={s} value={s}>{api.MONCO_STATUS_LABELS[s]}</option>
                ))}
              </select>
            </span>
          )}
        </div>
        {isOwner && totalCompleted > 0 && (
          <div className="ann-stats-right">
            <button
              className="btn-review-action"
              onClick={() => setSyntaxOpen(true)}
              disabled={autoBusy}
              title={autoBusy
                ? 'A plan is being processed automatically — wait for it to finish'
                : undefined}
            >
              Syntax Check
            </button>
            <button className="btn-review-action btn-select-all" onClick={selectAllCompleted}>
              {assignments.filter(a => a.status === 'completed').every(a => selected.has(a.id)) ? 'Deselect All' : 'Select All Completed'}
            </button>
            <button
              className="btn-review-action"
              onClick={handleBatchMatch}
              disabled={batchMatching || matchAllSt?.status === 'running' || autoBusy}
              title={autoBusy
                ? 'A plan is being processed automatically — wait for it to finish'
                : undefined}
            >
              {autoBusy && matchAllSt?.status !== 'running'
                ? <><span className="ann-btn-spinner" />Auto-processing…</>
                : matchAllSt?.status === 'running'
                ? <><span className="ann-btn-spinner" />Matching {matchAllSt.done ?? 0}/{matchAllSt.total ?? '?'}…</>
                : batchMatching
                  ? <><span className="ann-btn-spinner" />Starting…</>
                  : `Match All in Background (${totalCompleted})`}
            </button>
            <button
              className="btn-review-action"
              onClick={handleUploadAllLayouts}
              disabled={!!layoutUploadProgress || layoutUploadCount === 0}
              title="Push the detected layout of every uploaded plan into its Monco floor plan (replaces the rooms, zones, walls, doors and windows there)"
            >
              {layoutUploadProgress
                ? <><span className="ann-btn-spinner" />Uploading layouts {layoutUploadProgress.done}/{layoutUploadProgress.total}…</>
                : `Upload all layouts to DB (${layoutUploadCount})`}
            </button>
            <button
              className="btn-review-action"
              onClick={() => setCopyModalOpen(true)}
              disabled={!!orgCopy}
              title="Copy this project into another organization as a new Monco project (BETA)"
            >
              {orgCopy
                ? <>
                    <span className="ann-btn-spinner" />
                    {orgCopy.phase === 'creating' && 'Copying files…'}
                    {orgCopy.phase === 'uploading' && `Copying ${orgCopy.done}/${orgCopy.total}…`}
                    {orgCopy.phase === 'finishing' && 'Publishing…'}
                  </>
                : 'Copy to another organization'}
            </button>
          </div>
        )}
      </div>

      {isOwner && moncoLoadError && (
        <div className="ann-monco-notice text-dim">
          Monco status controls unavailable: {moncoLoadError}
        </div>
      )}

      {/* Cards — grouped by Monco upload when available, else a flat grid */}
      {assignments.length === 0 ? emptyState : useGrouped ? (
        <div className="ann-groups-scroll">
          {uploadSections.map((sec) => {
            const open = !collapsed.has(sec.key);
            return (
              <div key={sec.key} className="ann-upload-group">
                <div
                  className={`ann-upload-group-header${open ? ' is-open' : ''}`}
                  onClick={() => toggleCollapsed(sec.key)}
                  role="button"
                  tabIndex={0}
                  aria-expanded={open}
                  onKeyDown={(e) => {
                    if (e.target !== e.currentTarget) return;
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleCollapsed(sec.key); }
                  }}
                >
                  {(() => {
                    const full = sec.upload ? (sec.upload.name || `Upload ${sec.upload.id}`) : 'Ungrouped';
                    return (
                      <span className="ann-upload-group-title" title={sec.upload ? `${full} (upload ${sec.upload.id})` : full}>
                        <span className="ann-group-caret" aria-hidden>{open ? '▾' : '▸'}</span>
                        <span className="ann-upload-group-name">{sec.upload ? friendlyUploadName(sec.upload) : full}</span>
                        <span className="ann-upload-group-count">{sec.assignments.length}</span>
                      </span>
                    );
                  })()}
                  <div className="ann-upload-group-actions" onClick={(e) => e.stopPropagation()}>
                    <SelectAllCompletedButton
                      sectionAssignments={sec.assignments}
                      selectedIds={selected}
                      onToggle={setSectionSelection}
                    />
                    {(() => {
                      const prog = uploadAll[sec.key];
                      const completedCount = sec.assignments.filter((a) => a.status === 'completed').length;
                      return (
                        <button
                          className="btn-review-action btn-publish"
                          disabled={!!prog || completedCount === 0}
                          onClick={() => handleUploadAllToDb(sec.key, sec.assignments)}
                          title="Upload all completed assignments in this upload to the Monco DB (re-uploads any already uploaded)"
                        >
                          {prog
                            ? <><span className="ann-btn-spinner" />Uploading {prog.done}/{prog.total}…</>
                            : `Upload all to DB (${completedCount})`}
                        </button>
                      );
                    })()}
                    {(() => {
                      const prog = uploadAll[`pub:${sec.key}`];
                      const uploadedCount = sec.assignments.filter(
                        (a) => a.status === 'completed' && publishStatus[`${a.file}:${a.page}`]?.floor_plan_id,
                      ).length;
                      return (
                        <button
                          className="btn-review-action btn-publish"
                          disabled={!!prog || uploadedCount === 0}
                          onClick={() => handlePublishAll(sec.key, sec.assignments)}
                          title="Set publish status to 'published' in Monco for every uploaded plan in this group"
                        >
                          {prog
                            ? <><span className="ann-btn-spinner" />Publishing {prog.done}/{prog.total}…</>
                            : `Publish all (${uploadedCount})`}
                        </button>
                      );
                    })()}
                    {sec.upload && (
                      <label className="ann-upload-group-status">
                        <span className="dim">Status</span>
                        <select
                          className="btn-review-action"
                          value={sec.upload.status ?? ''}
                          disabled={statusBusy}
                          onChange={(e) => handleSetUploadStatus(sec.upload!.id, e.target.value as api.MoncoStatus)}
                          title={`Upload ${sec.upload.id} — change status in Monco`}
                        >
                          {sec.upload.status == null && <option value="" disabled>—</option>}
                          {api.MONCO_STATUS_VALUES.map((s) => (
                            <option key={s} value={s}>{api.MONCO_STATUS_LABELS[s]}</option>
                          ))}
                        </select>
                      </label>
                    )}
                  </div>
                </div>
                {open && (
                  <div className="ann-group-cards">{sec.assignments.map(renderCard)}</div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="ann-cards-grid">{assignments.map(renderCard)}</div>
      )}

      {/* Detail modal */}
      {detailAssignment && (
        <AssignmentDetailModal
          assignment={detailAssignment}
          autoState={autoByPlan.get(`${detailAssignment.file}:${detailAssignment.page}`)}
          uploadedFloorPlanId={publishStatus[`${detailAssignment.file}:${detailAssignment.page}`]?.floor_plan_id ?? null}
          uploadedPublishStatus={publishStatus[`${detailAssignment.file}:${detailAssignment.page}`]?.publish_status ?? null}
          moncoProjectId={moncoProjectId}
          moncoWebBase={moncoWebBase}
          projectId={projectId ?? null}
          layoutPage={layoutSt
            ? (layoutSt.files?.[fileStem(detailAssignment.file)]?.pages?.[String(detailAssignment.page)] ?? null)
            : undefined}
          layoutQualifies={!!layoutSt?.floor_plans?.[fileStem(detailAssignment.file)]?.includes(detailAssignment.page)}
          layoutBusy={layoutBusy.has(fileStem(detailAssignment.file))}
          onExtractLayout={() => handleExtractLayout(detailAssignment.file, [detailAssignment.page])}
          onClose={() => {
            setDetailAssignment(null);
            api.getPlanPublishStatus().then((s) => s && setPublishStatus(s));
          }}
        />
      )}
      <SyntaxCheckModal
        open={syntaxOpen}
        selection={selectedPlans}
        onClose={() => setSyntaxOpen(false)}
        onApplied={handleSyntaxApplied}
      />
      <CopyToOrganizationModal
        open={copyModalOpen}
        completedCount={totalCompleted}
        defaultProjectName={projectName || ''}
        onClose={() => setCopyModalOpen(false)}
        onCopy={handleCopyToOrganization}
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Assignment Card
   ═══════════════════════════════════════════════════════════════ */

// Upload-time prefix the S3 sync puts on file names ("1788848548459_642611_…").
const UPLOAD_TS_PREFIX = /^\d{10,}_/;

// Readable card title: the assignment name (default "<file stem> p.<n>")
// without the numeric upload-timestamp prefix and without the trailing page
// suffix, which the card shows on its own.
function cardTitle(a: PageAssignment): string {
  const raw = (a.name || fileStem(a.file) || `Page ${a.page}`).trim();
  const noPage = raw.replace(new RegExp(`\\s+p\\.?\\s*${a.page}$`, 'i'), '');
  return noPage.replace(UPLOAD_TS_PREFIX, '') || raw;
}

// Short added-on date for the card meta row: the year only when it differs.
function formatAddedShort(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(undefined, sameYear
    ? { day: 'numeric', month: 'short' }
    : { day: 'numeric', month: 'short', year: 'numeric' });
}

type SlotTone = 'muted' | 'info' | 'success' | 'accent' | 'warning' | 'error';
interface StatusSlot { label: string; value: string; tone: SlotTone; spinner?: boolean; tooltip: string }

const ANNOTATION_SLOT: Record<PageAssignment['status'], { value: string; tone: SlotTone }> = {
  assigned: { value: 'Assigned', tone: 'muted' },
  in_progress: { value: 'In progress', tone: 'info' },
  completed: { value: 'Completed', tone: 'success' },
  skipped: { value: 'Skipped', tone: 'muted' },
};

function layoutSlot(layout: LayoutBadge | null | undefined, statusLoaded: boolean, qualifies: boolean): StatusSlot {
  const base = { label: 'Layout' };
  if (!layout) {
    if (!statusLoaded) return { ...base, value: '—', tone: 'muted', tooltip: 'Layout status not loaded yet' };
    return qualifies
      ? { ...base, value: 'Not run', tone: 'muted', tooltip: 'Floor-plan layout not extracted yet' }
      : { ...base, value: '—', tone: 'muted', tooltip: 'Not a floor plan (triage), so no layout detection' };
  }
  const frac = layout.label.match(/(\d+)\/(\d+)/)?.[0];
  switch (layout.state) {
    case 'processing': return { ...base, value: `Running${frac ? ` ${frac}` : ''}`, tone: 'info', spinner: true, tooltip: layout.tooltip };
    case 'queued': return { ...base, value: 'Queued', tone: 'warning', tooltip: layout.tooltip };
    case 'failed': return { ...base, value: `Failed${frac ? ` ${frac}` : ''}`, tone: 'error', tooltip: layout.tooltip };
    case 'none': return { ...base, value: 'No layout', tone: 'muted', tooltip: layout.tooltip };
    case 'unscaled': return { ...base, value: 'No scale', tone: 'warning', tooltip: layout.tooltip };
    default: return { ...base, value: `Ready ${frac ?? ''}`.trim(), tone: 'success', tooltip: layout.tooltip };
  }
}

function AssignmentCard({ assignment: a, isOwner, published, selected, annotators, addedAt, matchState, autoState, layout, layoutStatusLoaded, layoutPage, layoutQualifies, layoutAction: layoutAct, layoutBusy, onExtractLayout, onReassign, onToggleSelect, onOpenDetail }: {
  assignment: PageAssignment;
  isOwner: boolean;
  published: boolean;
  selected: boolean;
  annotators: api.ProjectUser[];
  addedAt?: string;
  matchState?: api.PlanMatchState;
  autoState?: api.AutoProcessPlan;
  layout?: LayoutBadge | null;
  /** Layout status has been fetched (owner only). */
  layoutStatusLoaded?: boolean;
  /** This page's own layout record, if any. */
  layoutPage?: api.LayoutPageInfo | null;
  /** Triage lists at least one floor-plan page in this file. */
  layoutQualifies?: boolean;
  /** Owner's layout button for this file; null = hidden. */
  layoutAction?: LayoutAction | null;
  layoutBusy?: boolean;
  onExtractLayout?: () => void;
  onReassign: (newUserId: number) => void;
  onToggleSelect: () => void;
  onOpenDetail: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(a.name || `Page ${a.page}`);
  const [menuOpen, setMenuOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [layoutOpen, setLayoutOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const reassignRef = useRef<HTMLDivElement>(null);
  const projectId = useProjectStore((s) => s.project?.project_id);

  // Reassign options: the project's annotators, plus the current
  // assignee (in case their role changed) so the select reflects state.
  const reassignOptions = (() => {
    const opts = annotators.map((u) => ({ id: u.id, username: u.username }));
    if (a.user_id && !opts.some((o) => o.id === a.user_id)) {
      opts.unshift({ id: a.user_id, username: a.username || `User ${a.user_id}` });
    }
    return opts;
  })();

  const displayName = a.name || `Page ${a.page}`;
  const title = cardTitle(a);

  // Close the overflow menu / reassign popover on outside click or Escape.
  useEffect(() => {
    if (!menuOpen && !reassignOpen) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (menuOpen && !menuRef.current?.contains(t) && !menuBtnRef.current?.contains(t)) setMenuOpen(false);
      if (reassignOpen && !reassignRef.current?.contains(t)) setReassignOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setMenuOpen(false);
      setReassignOpen(false);
      menuBtnRef.current?.focus();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen, reassignOpen]);

  // Focus the first menu item when the menu opens (keyboard users).
  useEffect(() => {
    if (menuOpen) menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus();
  }, [menuOpen]);

  const onMenuKey = (e: ReactKeyboardEvent) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? []);
    const i = items.indexOf(document.activeElement as HTMLButtonElement);
    const next = e.key === 'ArrowDown' ? (i + 1) % items.length : (i - 1 + items.length) % items.length;
    items[next]?.focus();
  };

  const handleRename = async () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === displayName) { setEditing(false); return; }
    try {
      await api.pmRenameAssignment(a.id, trimmed);
      if (projectId) await useAssignmentStore.getState().load(projectId);
      useProjectStore.getState().setToast('Renamed', 'success');
    } catch {
      useProjectStore.getState().setToast('Rename failed', 'error');
    }
    setEditing(false);
  };

  const docLabel = a.doc_type?.replace('floor_plan_', '').replace(/_/g, ' ') || '';
  const canSelect = isOwner && a.status === 'completed';

  const slots: StatusSlot[] = [
    { label: 'Annotation', ...ANNOTATION_SLOT[a.status], tooltip: `Annotation: ${ANNOTATION_SLOT[a.status].value}` },
  ];
  if (isOwner) {
    slots.push(published
      ? { label: 'Publish', value: 'Published', tone: 'accent', tooltip: 'Published to Monco' }
      : { label: 'Publish', value: 'Not yet', tone: 'muted', tooltip: 'Not published to Monco yet' });
    slots.push(layoutSlot(layout, !!layoutStatusLoaded, !!layoutQualifies));
  }

  // Transient background work on this plan (auto-processing / Match All).
  const activity: { text: string; tone: SlotTone; spinner?: boolean; tooltip?: string }[] = [];
  if (autoState?.status === 'running') {
    activity.push({
      text: autoState.step === 'syntax_check' ? 'Syntax check…'
        : autoState.step === 'lv_match' ? 'Matching…'
        : autoState.step === 'publish' ? 'Publishing…'
        : 'Processing…',
      tone: 'info', spinner: true,
      tooltip: `The agent is processing this plan automatically (${autoState.step})`,
    });
  } else if (autoState?.status === 'failed') {
    activity.push({ text: 'Auto-processing failed', tone: 'error', tooltip: autoState.error });
  }
  if (matchState) {
    activity.push({
      text: matchState === 'pending' ? 'Match queued'
        : matchState === 'running' ? 'Matching…'
        : matchState === 'done' ? 'Matched'
        : matchState === 'skipped' ? 'Match skipped — being processed'
        : 'Match failed',
      tone: matchState === 'running' ? 'info' : matchState === 'done' ? 'success' : matchState === 'failed' ? 'error' : 'muted',
      spinner: matchState === 'running',
    });
  }

  // Layout action for the footer: running > view this page > retry > extract.
  const pageReady = layoutPage?.status === 'ok';
  const plural = (n: number) => `${n} page${n !== 1 ? 's' : ''}`;
  let layoutButton: ReactNode = null;
  let extraLayoutItem: { label: string; title: string } | null = null;
  if (isOwner && (layoutAct?.kind === 'running' || layoutBusy)) {
    layoutButton = (
      <button
        className="btn-review-action ann-card-btn"
        disabled
        title={layoutAct?.kind === 'running' ? `Layout detection running on ${plural(layoutAct.pages)}` : 'Submitting…'}
      >
        <span className="ann-btn-spinner" />Layout…
      </button>
    );
  } else if (isOwner && pageReady && projectId) {
    layoutButton = (
      <button
        className="btn-review-action ann-card-btn"
        onClick={() => setLayoutOpen(true)}
        title="Open the detected floor-plan layout (zones, rooms, walls) over the full plan"
      >
        View layout
      </button>
    );
    if (layoutAct && onExtractLayout) {
      extraLayoutItem = layoutAct.kind === 'retry'
        ? { label: 'Retry layout (other pages)', title: `Rerun layout detection on ${plural(layoutAct.pages)} failed, empty or missing floor-plan page(s) of this file` }
        : { label: 'Extract layout (other pages)', title: `Detect the floor-plan layout on ${plural(layoutAct.pages)} of this file` };
    }
  } else if (isOwner && layoutAct && onExtractLayout) {
    layoutButton = (
      <button
        className="btn-review-action ann-card-btn"
        onClick={onExtractLayout}
        title={layoutAct.kind === 'retry'
          ? `Rerun layout detection on ${layoutAct.pages} failed, empty or missing floor-plan page${layoutAct.pages !== 1 ? 's' : ''} of this file`
          : `Detect the floor-plan layout (zones, rooms, walls) on ${layoutAct.pages} page${layoutAct.pages !== 1 ? 's' : ''} of this file`}
      >
        {layoutAct.kind === 'retry' ? 'Retry layout' : 'Extract layout'}
      </button>
    );
  }

  const fullName = `${a.file} · p.${a.page}`;

  return (
    <div className={`ann-card ann-card-${a.status}${selected ? ' ann-card-selected' : ''}`}>
      {/* 1. Header: select · title · page · doc type */}
      <div className="ann-card-head">
        {isOwner && (
          <input
            type="checkbox"
            className="ann-card-checkbox"
            checked={selected}
            disabled={!canSelect}
            onChange={onToggleSelect}
            aria-label={`Select ${title}`}
            title={canSelect ? 'Select for Syntax Check / batch actions' : 'Only completed pages can be selected'}
          />
        )}
        {editing ? (
          <input
            className="ann-card-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={handleRename}
            onKeyDown={(e) => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') setEditing(false); }}
            aria-label="Assignment name"
            autoFocus
          />
        ) : isOwner ? (
          <button
            type="button"
            className="ann-card-title ann-card-name-editable"
            onClick={() => setEditing(true)}
            title={`${displayName}\n${fullName}\nClick to rename`}
          >
            {title}
          </button>
        ) : (
          <span className="ann-card-title" title={`${displayName}\n${fullName}`}>{title}</span>
        )}
        {!editing && <span className="ann-card-page" title={`Page ${a.page} of ${a.file}`}>p.{a.page}</span>}
        {docLabel && <span className="ann-card-doctype" title={a.doc_type ?? ''}>{docLabel}</span>}
      </div>

      {/* 2. Status: fixed slots in fixed order */}
      <div className={`ann-status-strip cols-${slots.length}`}>
        {slots.map((s) => (
          <div key={s.label} className="ann-status-slot" title={s.tooltip}>
            <span className="ann-slot-label">{s.label}</span>
            <span className={`ann-slot-value tone-${s.tone}`}>
              {s.spinner ? <span className="ann-slot-spinner" aria-hidden /> : <span className="ann-slot-dot" aria-hidden />}
              <span className="ann-slot-text">{s.value}</span>
            </span>
          </div>
        ))}
      </div>
      {activity.length > 0 && (
        <div className="ann-card-activity">
          {activity.map((x) => (
            <span key={x.text} className={`ann-slot-value tone-${x.tone}`} title={x.tooltip}>
              {x.spinner ? <span className="ann-slot-spinner" aria-hidden /> : <span className="ann-slot-dot" aria-hidden />}
              <span className="ann-slot-text">{x.text}</span>
            </span>
          ))}
        </div>
      )}

      {/* 3. Meta: assignee · added date */}
      <div className="ann-card-meta">
        {a.username ? (
          <span className="ann-card-assignee" title={`Assigned to ${a.username}`}>
            <span className="assignee-badge page-assignee" style={{ background: assigneeColor(a.username) }}>
              {a.username.slice(0, 2).toUpperCase()}
            </span>
            <span className="ann-card-assignee-name">{a.username}</span>
          </span>
        ) : <span className="ann-card-assignee dim">Unassigned</span>}
        {addedAt && (
          <span className="ann-card-added" title={`Added to S3 on ${new Date(addedAt).toLocaleString()}`}>
            Added {formatAddedShort(addedAt)}
          </span>
        )}
      </div>

      {/* 4. Footer actions (owner) */}
      {isOwner && (
        <div className="ann-card-footer">
          <button
            className="btn-review-action btn-publish ann-card-btn"
            onClick={onOpenDetail}
            disabled={a.status !== 'completed'}
            title={a.status === 'completed' ? 'Review elements, LV matches and publish' : 'Available once the annotation is completed'}
          >
            Review
          </button>
          {layoutButton}
          <div className="ann-card-more">
            <button
              ref={menuBtnRef}
              className="btn-review-action ann-card-btn ann-card-more-btn"
              aria-label="More actions"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => { setMenuOpen((o) => !o); setReassignOpen(false); }}
              title="More actions"
            >
              ⋯
            </button>
            {menuOpen && (
              <div ref={menuRef} className="ann-card-menu" role="menu" aria-label="Card actions" onKeyDown={onMenuKey}>
                <button role="menuitem" className="ann-card-menu-item"
                  onClick={() => { setMenuOpen(false); setReassignOpen(true); }}>
                  Reassign…
                </button>
                <button role="menuitem" className="ann-card-menu-item"
                  onClick={() => { setMenuOpen(false); setEditing(true); }}>
                  Rename…
                </button>
                {extraLayoutItem && onExtractLayout && (
                  <button role="menuitem" className="ann-card-menu-item" title={extraLayoutItem.title}
                    onClick={() => { setMenuOpen(false); onExtractLayout(); }}>
                    {extraLayoutItem.label}
                  </button>
                )}
              </div>
            )}
            {reassignOpen && (
              <div ref={reassignRef} className="ann-card-popover" role="dialog" aria-label="Reassign page">
                <label className="ann-card-popover-label" htmlFor={`reassign-${a.id}`}>Reassign to</label>
                <select
                  id={`reassign-${a.id}`}
                  className="btn-review-action"
                  value={a.user_id}
                  autoFocus
                  onChange={(e) => { onReassign(Number(e.target.value)); setReassignOpen(false); }}
                  title="Reassign this page to another annotator (re-sends the assignment email)"
                >
                  {reassignOptions.map((o) => (
                    <option key={o.id} value={o.id}>{o.username}</option>
                  ))}
                </select>
                <span className="ann-card-popover-hint">Re-sends the assignment email</span>
              </div>
            )}
          </div>
        </div>
      )}

      {layoutOpen && projectId && (
        <LayoutViewer
          projectId={projectId}
          file={a.file}
          fileStem={fileStem(a.file)}
          page={a.page}
          title={displayName}
          onClose={() => setLayoutOpen(false)}
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Assignment Detail Modal — elements, LV match, publish
   ═══════════════════════════════════════════════════════════════ */

function AssignmentDetailModal({ assignment: a, autoState, uploadedFloorPlanId, uploadedPublishStatus, moncoProjectId, moncoWebBase, projectId, layoutPage, layoutQualifies, layoutBusy, onExtractLayout, onClose }: {
  assignment: PageAssignment;
  autoState?: api.AutoProcessPlan;
  uploadedFloorPlanId: number | null;
  uploadedPublishStatus: api.PlanPublishStatus | null;
  moncoProjectId: number | null;
  moncoWebBase: string;
  projectId: string | null;
  /** This page's layout record: undefined = status not loaded (button hidden), null = no record. */
  layoutPage?: api.LayoutPageInfo | null;
  /** Triage says this page is a floor plan, so "Extract layout" can run on it. */
  layoutQualifies: boolean;
  layoutBusy: boolean;
  onExtractLayout: () => void;
  onClose: () => void;
}) {
  const [layoutOpen, setLayoutOpen] = useState(false);
  const [items, setItems] = useState<ReviewBoqItem[]>([]);
  const [matchSummary, setMatchSummary] = useState<MatchSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [matching, setMatching] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState<{ location: string; artifacts: number } | null>(null);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [uploadingToDb, setUploadingToDb] = useState(false);
  const [uploadingLayout, setUploadingLayout] = useState(false);
  // Seed from the persisted upload status so reopening the modal still
  // shows the floor_plan_id from the last upload (kept server-side in
  // published_plans.json). A fresh upload overwrites this below.
  const [uploadDbResult, setUploadDbResult] = useState<{ floorPlanId: number } | null>(
    uploadedFloorPlanId ? { floorPlanId: uploadedFloorPlanId } : null);
  // Current Monco publish status for this plan. Seeded from the persisted
  // value; a fresh upload resets it to 'unpublished' (the server default).
  const [publishStatusValue, setPublishStatusValue] = useState<api.PlanPublishStatus>(
    uploadedPublishStatus ?? 'unpublished');
  const [settingStatus, setSettingStatus] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const boqExtraction = useProjectStore((s) => s.boqExtraction);

  // Deep link into the Monco web app for the uploaded floor plan
  // (/project/{id}/smart-surveyor?floorPlanId=...). Built client-side
  // once the plan has a floor_plan_id and we know the project + web base.
  const floorPlanId = uploadDbResult?.floorPlanId ?? null;
  const moncoUrl = (floorPlanId && moncoProjectId && moncoWebBase)
    ? `${moncoWebBase}/project/${moncoProjectId}/smart-surveyor?floorPlanId=${floorPlanId}`
    : null;

  // Load match data for this assignment
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getPlanReviewData(a.file, a.page) as any;
      if (data && Array.isArray(data) && data.length > 0) {
        setItems(data[0].boq_items || []);
        setMatchSummary(data[0].match_summary || null);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [a.file, a.page]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleMatch = async () => {
    setMatching(true);
    const result = await api.matchPlanResult(a.file, a.page);
    if (result.ok && result.data) {
      setItems(result.data.boq_items as ReviewBoqItem[]);
      setMatchSummary(result.data.match_summary || null);
      useProjectStore.getState().setToast('Matched successfully', 'success');
    } else if (result.status === 409) {
      useProjectStore.getState().setToast(
        'Plan is currently being processed — try again when it finishes', 'error');
    } else {
      useProjectStore.getState().setToast(`Match failed: ${result.error}`, 'error');
    }
    setMatching(false);
  };

  const handleMatchSelect = async (index: number, pos: BoqPosition | null) => {
    const lvMatch: LvMatch | null = pos ? {
      lvId: pos.position_number || '', lvName: pos.description || '',
      confidence: 'manual', reasoning: 'Manual selection', matchPass: 'manual',
      lvQuantity: pos.quantity, lvUnit: pos.unit,
    } : null;
    setEditIndex(null);
    const res = await api.updatePlanMatchItemResult(a.file, a.page, index, lvMatch);
    if (res.ok && res.data) {
      setItems(res.data.boq_items as ReviewBoqItem[]);
      setMatchSummary((res.data.match_summary as MatchSummary) || null);
    } else if (res.status === 409) {
      useProjectStore.getState().setToast(
        'Edit not saved — plan is currently being processed, try again when it finishes', 'error');
    } else {
      useProjectStore.getState().setToast(`Match update failed: ${res.error}`, 'error');
    }
  };

  const tryPublish = () => {
    if (matchSummary && matchSummary.unmatched > 0) {
      setShowPublishConfirm(true);
      return;
    }
    doPublish();
  };

  const doPublish = async () => {
    setShowPublishConfirm(false);
    setPublishing(true);
    try {
      const result = await api.publishPlansReview(a.file, a.page) as any;
      if (result) {
        setPublishResult({ location: result.location, artifacts: result.artifacts });
        useProjectStore.getState().setToast('Published!', 'success');
      }
    } catch (e) {
      useProjectStore.getState().setToast(`Publish failed: ${e}`, 'error');
    }
    setPublishing(false);
  };

  // Upload-to-DB runs the full Monco-application upload flow in one
  // click: login → compress image → upload image+PDF → import. The
  // backend orchestrates everything via /api/review/plans/upload-to-db
  // (see review.py:api_review_plans_upload_to_db). Requires per-project
  // monco_project_id in project.json and the MONCO_* env vars on the
  // viewer server.
  const handleUploadToDb = async () => {
    setUploadingToDb(true);
    try {
      const result = await api.uploadPlanToDb(a.file, a.page);
      if (result?.floor_plan_id) {
        setUploadDbResult({ floorPlanId: result.floor_plan_id });
        // A fresh import always lands unpublished server-side.
        setPublishStatusValue('unpublished');
        const replaced = result.deleted_floor_plan_id
          ? ` (replaced ${result.deleted_floor_plan_id})`
          : '';
        useProjectStore.getState().setToast(
          `Uploaded to DB — floor_plan_id ${result.floor_plan_id}${replaced}`, 'success');
      }
    } catch (e) {
      useProjectStore.getState().setToast(`Upload to DB failed: ${e}`, 'error');
    }
    setUploadingToDb(false);
  };

  // Push the detected layout into the uploaded Monco floor plan. Needs the
  // floor_plan_id from "Upload to DB" and an 'ok' layout record.
  const handleUploadLayoutToDb = async () => {
    setUploadingLayout(true);
    const res = await api.uploadPlanLayoutToDbResult(a.file, a.page);
    if (res.ok && res.data) {
      const d = res.data;
      const skipped = Object.entries(d.skipped || {}).map(([k, n]) => `${n} ${k} skipped`).join(', ');
      useProjectStore.getState().setToast(
        `Layout uploaded — ${d.zonesCreated} zones, ${d.roomsCreated} rooms, ${d.wallsCreated} walls, `
        + `${d.doorsCreated} doors, ${d.windowsCreated} windows`
        + (d.scaleApplied ? ` · scale applied (${d.routesRecomputed} routes re-measured)` : '')
        + (skipped ? ` · ${skipped}` : ''),
        'success');
    } else {
      useProjectStore.getState().setToast(`Layout upload failed: ${res.error || res.status}`, 'error');
    }
    setUploadingLayout(false);
  };

  // Change the floor plan's publish status in Monco. Available only once
  // the plan has a floor_plan_id (i.e. after Upload to DB).
  const handleSetPublishStatus = async (next: api.PlanPublishStatus) => {
    const fpId = uploadDbResult?.floorPlanId;
    if (!fpId || next === publishStatusValue) return;
    setSettingStatus(true);
    try {
      const res = await api.setPlanPublishStatus(a.file, a.page, next);
      const applied = res?.publish_status ?? next;
      setPublishStatusValue(applied);
      useProjectStore.getState().setToast(
        `floor_plan_id ${fpId} → ${applied}`, 'success');
    } catch (e) {
      useProjectStore.getState().setToast(`Failed to set publish status: ${e}`, 'error');
    }
    setSettingStatus(false);
  };

  const elements = items.filter((i) => i.text !== 'floor_plan_route');
  const routes = items.filter((i) => i.text === 'floor_plan_route');
  const hasMatched = !!matchSummary;
  const displayName = a.name || `Page ${a.page}`;
  // The agent's worker is processing this plan right now — the server
  // rejects a concurrent Upload to DB with 409, so surface it as loading
  // instead of letting the click bounce.
  const autoRunning = autoState?.status === 'running';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="ann-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ann-detail-header">
          <div>
            <h3>{displayName}</h3>
            <div className="ann-detail-file">{a.file} &middot; p.{a.page}</div>
          </div>
          <button className="ann-detail-close" onClick={onClose}>&times;</button>
        </div>

        {!loading && (
          <>
            {/* Match summary */}
            {matchSummary && (
              <div className="ann-detail-summary">
                <span className="ann-stat-badge ann-stat-completed">
                  {matchSummary.matched}/{matchSummary.totalBoqItems} matched
                </span>
                {matchSummary.matchedHigh > 0 && <span className="ann-stat-badge review-confidence-high">{matchSummary.matchedHigh}H</span>}
                {matchSummary.matchedMedium > 0 && <span className="ann-stat-badge review-confidence-medium">{matchSummary.matchedMedium}M</span>}
                {matchSummary.matchedLow > 0 && <span className="ann-stat-badge review-confidence-low">{matchSummary.matchedLow}L</span>}
              </div>
            )}

            {/* Actions */}
            <div className="ann-detail-actions">
              <button
                className="btn-review-action"
                onClick={handleMatch}
                disabled={matching || publishing || uploadingToDb || autoRunning}
                title={autoRunning ? `The agent is processing this plan automatically (${autoState?.step})` : undefined}
              >
                {matching
                  ? <><span className="ann-btn-spinner" />Matching...</>
                  : autoRunning
                    ? <><span className="ann-btn-spinner" />Auto-processing...</>
                    : hasMatched ? 'Re-match' : 'Match with LV'}
              </button>
              <button className="btn-review-action btn-publish" onClick={tryPublish} disabled={publishing || matching || uploadingToDb}>
                {publishing
                  ? <><span className="ann-btn-spinner" />Publishing...</>
                  : 'Publish'}
              </button>
              <button
                className="btn-review-action btn-publish"
                onClick={handleUploadToDb}
                disabled={uploadingToDb || publishing || matching || autoRunning}
                title={autoRunning
                  ? `The agent is processing this plan automatically (${autoState?.step})`
                  : 'Login to Monco API, compress + upload image and PDF, then import as a floor plan in one shot'}
              >
                {uploadingToDb
                  ? <><span className="ann-btn-spinner" />Uploading to DB...</>
                  : autoRunning
                    ? <><span className="ann-btn-spinner" />Auto-processing...</>
                    : uploadDbResult ? 'Re-upload to DB' : 'Upload to DB'}
              </button>
              {/* Publish-status select — appears once the plan has a
                  floor_plan_id (after Upload to DB). Lets the owner flip
                  the Monco floor plan between unpublished/published. */}
              {uploadDbResult && (
                <select
                  className="btn-review-action"
                  value={publishStatusValue}
                  disabled={settingStatus || uploadingToDb}
                  onChange={(e) => handleSetPublishStatus(e.target.value as api.PlanPublishStatus)}
                  title={`floor_plan_id ${uploadDbResult.floorPlanId} — change publish status in Monco`}
                >
                  <option value="unpublished">Unpublished</option>
                  <option value="published">Published</option>
                </select>
              )}
              {settingStatus && <span className="ann-btn-spinner" />}
              {/* Open this floor plan in the Monco web app — appears once
                  it has a floor_plan_id (after Upload to DB). */}
              {uploadDbResult && moncoUrl && (
                <a
                  className="btn-review-action btn-open-monco"
                  href={moncoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={`Open floor_plan_id ${uploadDbResult.floorPlanId} in Monco`}
                >
                  Open in Monco ↗
                </a>
              )}
              {projectId && layoutPage !== undefined && (() => {
                const st = layoutPage?.status;
                const why = layoutPage?.error || (st === 'no_layout' ? layoutPage?.reason : '');
                const err = why ? ` — ${why}` : '';
                if (st === 'ok') {
                  const canUploadLayout = !!uploadDbResult && !uploadingLayout && !uploadingToDb;
                  return (
                    <>
                      <button className="btn-review-action" onClick={() => setLayoutOpen(true)}
                        title="Open the detected floor-plan layout (zones, rooms, walls) over the full plan">
                        Layout
                      </button>
                      {/* Disabled buttons swallow hover in some browsers: the tooltip sits on a wrapper. */}
                      <span style={{ display: 'inline-flex' }}
                        title={uploadDbResult
                          ? `Replace the rooms, zones, walls, doors and windows of floor_plan_id ${uploadDbResult.floorPlanId} with this layout`
                          : 'Upload to DB first'}>
                        <button className="btn-review-action btn-publish" onClick={handleUploadLayoutToDb}
                          disabled={!canUploadLayout}>
                          {uploadingLayout
                            ? <><span className="ann-btn-spinner" />Uploading layout...</>
                            : 'Upload layout to DB'}
                        </button>
                      </span>
                    </>
                  );
                }
                const running = st === 'queued' || st === 'processing' || layoutBusy;
                const failure = st === 'stale' ? `Layout detection stalled${err}`
                  : st === 'no_layout' ? `No layout found${err}`
                  : st ? `Layout detection ${st === 'unknown' ? 'record unreadable' : 'failed'}${err}`
                  : '';
                if (!running && layoutQualifies && (!st || LAYOUT_RETRY.includes(st))) {
                  return (
                    <button className="btn-review-action" onClick={onExtractLayout}
                      title={failure
                        ? `${failure}. Click to rerun layout detection on this page`
                        : 'Detect the floor-plan layout (zones, rooms, walls) on this page'}>
                      Extract layout
                    </button>
                  );
                }
                const label = running ? 'Layout…' : st ? 'Layout' : 'No layout';
                const reason = layoutBusy ? 'Submitting…'
                  : running ? `Layout detection ${st}…`
                  : st === 'no_layout' ? `No layout found on this page${err}`
                  : failure || 'Not a floor plan (triage), so no layout detection';
                // Disabled buttons swallow hover in some browsers: the tooltip sits on a wrapper.
                return (
                  <span title={reason} style={{ display: 'inline-flex' }}>
                    <button className="btn-review-action" disabled>
                      {running && <span className="ann-btn-spinner" />}{label}
                    </button>
                  </span>
                );
              })()}
            </div>
          </>
        )}

        {layoutOpen && projectId && (
          <LayoutViewer
            projectId={projectId}
            file={a.file}
            fileStem={fileStem(a.file)}
            page={a.page}
            title={displayName}
            onClose={() => setLayoutOpen(false)}
          />
        )}

        {publishResult && (
          <div className="ann-detail-publish-result">
            Published {publishResult.artifacts} artifacts to {publishResult.location}
          </div>
        )}

        {uploadDbResult && (
          <div className="ann-detail-publish-result">
            Uploaded to DB — floor_plan_id <strong>{uploadDbResult.floorPlanId}</strong>
          </div>
        )}

        {/* Content */}
        <div className="ann-detail-content">
          {loading ? (
            <div className="ann-detail-loader">
              <div className="ann-loader-spinner" />
              <div className="ann-loader-text">Loading annotation data...</div>
            </div>
          ) : items.length === 0 ? (
            <div className="dim" style={{ padding: 16 }}>No elements yet. Click "Match with LV" to match annotation groups to LV positions.</div>
          ) : (
            <>
              {elements.length > 0 && (
                <div className="ann-detail-section">
                  <div className="ann-detail-section-title">Elements ({elements.length})</div>
                  <table className="review-table">
                    <thead>
                      <tr>
                        <th>Group</th><th>Spec</th><th>Qty</th><th>LV Match</th><th>Conf</th>
                      </tr>
                    </thead>
                    <tbody>
                      {elements.map((item, idx) => (
                        <ModalReviewRow
                          key={idx}
                          item={item}
                          index={idx}
                          editing={editIndex === idx}
                          onEditToggle={() => setEditIndex(editIndex === idx ? null : idx)}
                          onMatchSelect={handleMatchSelect}
                          boqPositions={boqExtraction?.positions ?? []}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {routes.length > 0 && (
                <div className="ann-detail-section">
                  <div className="ann-detail-section-title">Routes ({routes.length})</div>
                  <table className="review-table">
                    <thead>
                      <tr>
                        <th>Type</th><th>Spec</th><th>Length</th><th>LV Match</th><th>Conf</th>
                      </tr>
                    </thead>
                    <tbody>
                      {routes.map((item, idx) => {
                        const realIdx = elements.length + idx;
                        return (
                          <ModalReviewRow
                            key={realIdx}
                            item={item}
                            index={realIdx}
                            editing={editIndex === realIdx}
                            onEditToggle={() => setEditIndex(editIndex === realIdx ? null : realIdx)}
                            onMatchSelect={handleMatchSelect}
                            boqPositions={boqExtraction?.positions ?? []}
                          />
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        {/* Publish confirmation modal */}
        {showPublishConfirm && matchSummary && (
          <div className="ann-confirm-overlay" onClick={() => setShowPublishConfirm(false)}>
            <div className="ann-confirm-modal" onClick={(e) => e.stopPropagation()}>
              <div className="ann-confirm-icon">!</div>
              <h4>Unmatched Elements</h4>
              <p>
                <strong>{matchSummary.unmatched}</strong> of <strong>{matchSummary.totalBoqItems}</strong> elements
                are not matched with LV positions.
              </p>
              <p className="dim">Unmatched elements will be published without LV references.</p>
              <div className="ann-confirm-actions">
                <button className="btn-review-action" onClick={() => setShowPublishConfirm(false)}>
                  Go Back
                </button>
                <button className="btn-review-action btn-publish" onClick={doPublish}>
                  Publish Anyway
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Modal Review Row + Match Dropdown
   ═══════════════════════════════════════════════════════════════ */

function ModalReviewRow({ item, index, editing, onEditToggle, onMatchSelect, boqPositions }: {
  item: ReviewBoqItem;
  index: number;
  editing: boolean;
  onEditToggle: () => void;
  onMatchSelect: (index: number, pos: BoqPosition | null) => void;
  boqPositions: BoqPosition[];
}) {
  const match = item.lvMatch;
  const confClass = match ? `review-row-conf-${match.confidence}` : 'review-row-conf-none';
  const isRoute = item.text === 'floor_plan_route';
  const triggerRef = useRef<HTMLTableCellElement>(null);

  return (
    <tr className={confClass}>
      <td>{item.item}</td>
      <td className="review-spec-cell" title={item.text}>{isRoute ? '-' : item.text || '-'}</td>
      <td className="qty">{item.quantity}{isRoute ? ' m' : ''}</td>
      <td ref={triggerRef} className="review-match-cell" onClick={(e) => { e.stopPropagation(); onEditToggle(); }}>
        {match ? (
          <span title={match.reasoning}>{match.lvId}</span>
        ) : (
          <span className="review-match-assign">Assign LV</span>
        )}
        {editing && (
          <ModalMatchDropdown
            triggerRef={triggerRef}
            onSelect={(pos) => onMatchSelect(index, pos)}
            onClose={onEditToggle}
            currentMatch={match}
            positions={boqPositions}
          />
        )}
      </td>
      <td>{match && <ConfidenceBadge confidence={match.confidence} />}</td>
    </tr>
  );
}

function ModalMatchDropdown({ triggerRef, onSelect, onClose, currentMatch, positions }: {
  triggerRef: React.RefObject<HTMLElement | null>;
  onSelect: (pos: BoqPosition | null) => void;
  onClose: () => void;
  currentMatch?: LvMatch | null;
  positions: BoqPosition[];
}) {
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  // Position dropdown relative to trigger element
  useEffect(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const dropH = 300;
      const spaceBelow = window.innerHeight - rect.bottom;
      const top = spaceBelow >= dropH ? rect.bottom + 2 : rect.top - dropH - 2;
      const left = Math.min(rect.left, window.innerWidth - 370);
      setPos({ top: Math.max(4, top), left: Math.max(4, left) });
    }
  }, [triggerRef]);

  const filtered = useMemo(() => {
    if (!search) return positions.slice(0, 50);
    const lower = search.toLowerCase();
    return positions.filter((p) => `${p.position_number || ''} ${p.description || ''}`.toLowerCase().includes(lower)).slice(0, 50);
  }, [positions, search]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  return (
    <div className="match-dropdown ann-modal-dropdown" ref={dropdownRef} style={{ top: pos.top, left: pos.left }} onClick={(e) => e.stopPropagation()}>
      <input className="match-dropdown-search" type="text" placeholder="Search LV positions..." value={search} onChange={(e) => setSearch(e.target.value)} autoFocus onClick={(e) => e.stopPropagation()} />
      <div className="match-dropdown-list">
        {currentMatch && <div className="match-dropdown-item match-dropdown-clear" onClick={(e) => { e.stopPropagation(); onSelect(null); }}>Clear match</div>}
        {filtered.map((pos, i) => (
          <div key={`${pos.position_number}-${i}`} className="match-dropdown-item" title={`${pos.position_number} — ${pos.description}${pos.quantity != null ? ` (${pos.quantity} ${pos.unit || ''})` : ''}`} onClick={(e) => { e.stopPropagation(); onSelect(pos); }}>
            <span className="match-dd-id">{pos.position_number}</span>
            <span className="match-dd-desc">{pos.description}</span>
            {pos.quantity != null && <span className="match-dd-qty">{pos.quantity} {pos.unit}</span>}
          </div>
        ))}
        {filtered.length === 0 && <div className="match-dropdown-empty">No positions found</div>}
      </div>
    </div>
  );
}

function ConfidenceBadge({ confidence }: { confidence: string }) {
  const labels: Record<string, string> = { high: 'HIGH', medium: 'MED', low: 'LOW', manual: 'MANUAL' };
  const conf = confidence || 'unknown';
  return (
    <span className={`review-confidence-badge review-confidence-${conf}`}>
      {labels[conf] || conf.toUpperCase()}
    </span>
  );
}
